import { useState, useMemo } from 'react'
import { Link } from 'react-router'
import { Calculator, ArrowRight, Home, Building2, Sparkles, Info } from 'lucide-react'

/**
 * Surface m² → budget travaux estimator. Standalone tool for the
 * /budget-travaux landing page. Targets ~12k/mo French queries
 * "prix renovation au m2", "budget travaux maison 100m2" etc.
 *
 * Math: budget = surface × pricePerSqm[scope] × propertyMult × regionMult.
 * Benchmarks are France-wide 2026 ranges (DB has insufficient real lead
 * data per (svc, city) to localize). Disclosed honestly to user.
 *
 * Why a separate component vs reusing PriceEstimator:
 *   - PriceEstimator targets single-service per-m² pricing on /prix/* pages
 *   - BudgetEstimator targets multi-trade project budgets across all corps
 *     d'état (gros œuvre + plomberie + élec + finitions). Different math.
 */

type ProjectType = 'appartement' | 'maison'
type ProjectScope = 'rafraichissement' | 'partielle' | 'complete' | 'lourde'
type Region = 'paris' | 'idf' | 'metropole' | 'moyenne' | 'rural'

const SCOPE_RANGES: Record<ProjectScope, { min: number; max: number; label: string; desc: string }> = {
  rafraichissement: {
    min: 250, max: 500,
    label: 'Rafraîchissement',
    desc: 'Peinture, sols, petites finitions sans travaux structurels',
  },
  partielle: {
    min: 500, max: 1000,
    label: 'Rénovation partielle',
    desc: 'Cuisine OU salle de bain + peinture + sols',
  },
  complete: {
    min: 1000, max: 1800,
    label: 'Rénovation complète',
    desc: 'Tous les corps d\'état : électricité, plomberie, sols, peinture, cuisine, SDB',
  },
  lourde: {
    min: 1800, max: 2800,
    label: 'Rénovation lourde / restructuration',
    desc: 'Modification de cloisons, ouvertures de murs, isolation, mise aux normes complète',
  },
}

const REGION_MULT: Record<Region, { mult: number; label: string }> = {
  paris: { mult: 1.25, label: 'Paris' },
  idf: { mult: 1.15, label: 'Île-de-France' },
  metropole: { mult: 1.10, label: 'Grandes métropoles (Lyon, Marseille, Bordeaux…)' },
  moyenne: { mult: 1.00, label: 'Villes moyennes' },
  rural: { mult: 0.90, label: 'Zones rurales' },
}

const PROPERTY_MULT: Record<ProjectType, { mult: number }> = {
  appartement: { mult: 1.0 },
  maison: { mult: 1.05 },
}

// Cost breakdown by scope — % of total budget per trade
const BREAKDOWN: Record<ProjectScope, Array<{ trade: string; pct: number }>> = {
  rafraichissement: [
    { trade: 'Peinture', pct: 50 },
    { trade: 'Sols (parquet, carrelage)', pct: 30 },
    { trade: 'Petites finitions, électricité légère', pct: 20 },
  ],
  partielle: [
    { trade: 'Cuisine ou salle de bain', pct: 40 },
    { trade: 'Plomberie', pct: 15 },
    { trade: 'Électricité', pct: 15 },
    { trade: 'Peinture', pct: 15 },
    { trade: 'Sols', pct: 15 },
  ],
  complete: [
    { trade: 'Plomberie + sanitaires', pct: 15 },
    { trade: 'Électricité + tableau', pct: 15 },
    { trade: 'Cuisine équipée', pct: 18 },
    { trade: 'Salle de bain', pct: 12 },
    { trade: 'Peinture + plâtrerie', pct: 15 },
    { trade: 'Sols (parquet, carrelage)', pct: 15 },
    { trade: 'Menuiserie + portes', pct: 10 },
  ],
  lourde: [
    { trade: 'Gros œuvre + maçonnerie', pct: 18 },
    { trade: 'Isolation', pct: 12 },
    { trade: 'Plomberie + sanitaires', pct: 13 },
    { trade: 'Électricité + tableau', pct: 13 },
    { trade: 'Cuisine + salle de bain', pct: 18 },
    { trade: 'Plâtrerie + cloisons', pct: 8 },
    { trade: 'Sols + finitions', pct: 12 },
    { trade: 'Menuiserie', pct: 6 },
  ],
}

export function BudgetEstimator() {
  const [type, setType] = useState<ProjectType>('appartement')
  const [scope, setScope] = useState<ProjectScope>('complete')
  const [surface, setSurface] = useState(60)
  const [region, setRegion] = useState<Region>('paris')

  const result = useMemo(() => {
    const r = SCOPE_RANGES[scope]
    const regionM = REGION_MULT[region].mult
    const propM = PROPERTY_MULT[type].mult
    const min = Math.round(r.min * surface * regionM * propM)
    const max = Math.round(r.max * surface * regionM * propM)
    const median = Math.round((min + max) / 2)
    const minPerSqm = Math.round(r.min * regionM * propM)
    const maxPerSqm = Math.round(r.max * regionM * propM)
    return { min, median, max, minPerSqm, maxPerSqm }
  }, [type, scope, surface, region])

  const breakdown = BREAKDOWN[scope].map((b) => ({
    ...b,
    minAmount: Math.round((result.min * b.pct) / 100),
    maxAmount: Math.round((result.max * b.pct) / 100),
  }))

  return (
    <section className="rounded-2xl border border-primary/20 bg-gradient-to-br from-blue-50 to-indigo-50 p-6 lg:p-8">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary text-white">
          <Calculator className="h-5 w-5" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-slate-900">Estimez votre budget travaux</h2>
          <p className="text-sm text-slate-500">Résultat instantané selon surface, type et région</p>
        </div>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <div className="space-y-5">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Type de bien</label>
            <div className="mt-2 grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setType('appartement')}
                className={`flex items-center justify-center gap-2 rounded-lg border px-3 py-2.5 text-sm font-semibold transition-colors ${
                  type === 'appartement' ? 'border-primary bg-primary text-white' : 'border-slate-300 bg-white text-slate-700 hover:border-primary'
                }`}
              >
                <Building2 className="h-4 w-4" /> Appartement
              </button>
              <button
                type="button"
                onClick={() => setType('maison')}
                className={`flex items-center justify-center gap-2 rounded-lg border px-3 py-2.5 text-sm font-semibold transition-colors ${
                  type === 'maison' ? 'border-primary bg-primary text-white' : 'border-slate-300 bg-white text-slate-700 hover:border-primary'
                }`}
              >
                <Home className="h-4 w-4" /> Maison
              </button>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between text-sm">
              <label htmlFor="surface" className="font-semibold text-slate-700">Surface</label>
              <span className="rounded-md bg-white px-2.5 py-1 font-bold text-primary shadow-xs">{surface} m²</span>
            </div>
            <input
              id="surface"
              type="range"
              min="20"
              max="300"
              step="5"
              value={surface}
              onChange={(e) => setSurface(parseInt(e.target.value))}
              className="mt-2 w-full accent-primary"
            />
            <div className="mt-1 flex justify-between text-[10px] text-slate-400">
              <span>20 m²</span><span>300 m²</span>
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Type de chantier</label>
            <div className="mt-2 space-y-2">
              {(Object.keys(SCOPE_RANGES) as ProjectScope[]).map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setScope(s)}
                  className={`w-full rounded-lg border px-3 py-2 text-left transition-colors ${
                    scope === s ? 'border-primary bg-white' : 'border-slate-200 bg-white/50 hover:border-primary'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className={`text-sm font-semibold ${scope === s ? 'text-primary' : 'text-slate-700'}`}>
                      {SCOPE_RANGES[s].label}
                    </span>
                    <span className="text-[10px] font-medium text-slate-400">{SCOPE_RANGES[s].min}–{SCOPE_RANGES[s].max} €/m²</span>
                  </div>
                  <p className="mt-0.5 text-xs text-slate-500">{SCOPE_RANGES[s].desc}</p>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label htmlFor="region" className="text-xs font-semibold uppercase tracking-wider text-slate-500">Région</label>
            <select
              id="region"
              value={region}
              onChange={(e) => setRegion(e.target.value as Region)}
              className="mt-2 w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm font-medium text-slate-700 focus:border-primary focus:outline-none"
            >
              {(Object.keys(REGION_MULT) as Region[]).map((r) => (
                <option key={r} value={r}>{REGION_MULT[r].label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="space-y-4">
          <div className="rounded-2xl bg-white p-6 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Budget estimé</p>
            <p className="mt-2 text-3xl font-bold text-slate-900">
              {result.min.toLocaleString('fr-FR')}–{result.max.toLocaleString('fr-FR')} €
            </p>
            <p className="mt-1 text-sm text-slate-500">
              Soit {result.minPerSqm}–{result.maxPerSqm} €/m² pour {SCOPE_RANGES[scope].label.toLowerCase()}
            </p>
            <div className="mt-3 rounded-lg bg-amber-50 p-3 text-xs text-amber-900">
              <Info className="mr-1 inline h-3 w-3" />
              <strong>Médiane :</strong> {result.median.toLocaleString('fr-FR')} €. Marge ±15 % selon état du bien et matériaux choisis.
            </div>
          </div>

          <div className="rounded-2xl bg-white p-6 shadow-sm">
            <h3 className="text-sm font-bold text-slate-900">Répartition du budget par poste</h3>
            <div className="mt-3 space-y-2">
              {breakdown.map((b) => (
                <div key={b.trade} className="flex items-baseline justify-between gap-2 text-sm">
                  <span className="text-slate-600">{b.trade}</span>
                  <span className="shrink-0 font-semibold text-slate-900">
                    {b.minAmount.toLocaleString('fr-FR')}–{b.maxAmount.toLocaleString('fr-FR')} €
                  </span>
                </div>
              ))}
            </div>
          </div>

          <Link
            to={`/devis-gratuit?surface=${surface}&scope=${scope}&type=${type}&region=${region}`}
            className="flex items-center justify-between gap-3 rounded-xl bg-primary px-5 py-4 text-white shadow-md transition-colors hover:bg-primary-hover btn-press"
          >
            <div>
              <p className="text-sm font-bold">Recevez 3 devis gratuits pour ce projet</p>
              <p className="text-xs text-blue-100">Comparez le devis réel à cette estimation</p>
            </div>
            <ArrowRight className="h-5 w-5 shrink-0" />
          </Link>
        </div>
      </div>

      <p className="mt-5 flex items-start gap-2 text-[11px] leading-relaxed text-slate-500">
        <Sparkles className="mt-0.5 h-3 w-3 shrink-0 text-amber-500" />
        Estimation indicative basée sur les fourchettes de prix moyens 2026. Les tarifs réels varient selon l'état du bien, l'accessibilité, les matériaux choisis et la disponibilité des artisans. Pour un chiffrage précis, demandez 3 devis gratuits.
      </p>
    </section>
  )
}
